import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import { collection, query, where, getDocs, orderBy, deleteDoc, doc, getDoc, updateDoc } from "firebase/firestore";
import "../../admin/AdminDashboard.css";

export default function AdminClues() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [clues, setClues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trailName, setTrailName] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadTrail() {
      try {
        const trailRef = doc(db, "trails", id);
        const trailSnap = await getDoc(trailRef);

        if (trailSnap.exists()) {
          setTrailName(trailSnap.data().name);
        } else {
          setError("Trail not found.");
        }
      } catch (err) {
        setError("Failed to load trail information.");
      }
    }

    loadTrail();
  }, [id]);

  useEffect(() => {
    async function loadClues() {
      setLoading(true);
      setError("");
      try {
        const q = query(
          collection(db, "clues"),
          where("trailId", "==", id),
          orderBy("order", "asc")
        );

        const snap = await getDocs(q);
        const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        setClues(list);
      } catch (err) {
        setError("Failed to load clues. Please try again.");
      } finally {
        setLoading(false);
      }
    }

    loadClues();
  }, [id]);

  async function deleteClue(clueId) {
    if (!window.confirm("Delete this clue?")) return;

    try {
      await deleteDoc(doc(db, "clues", clueId));
      setClues(prev => prev.filter(c => c.id !== clueId));
    } catch (err) {
      setError("Failed to delete clue. Please try again.");
    }
  }

  async function deleteCaseFile(clueId, fileUrl) {
    if (!window.confirm("Delete this case file?")) return;

    try {
      const clue = clues.find(c => c.id === clueId);
      if (!clue) return;

      const updatedCaseFiles = (clue.caseFiles || []).filter(url => url !== fileUrl);

      await updateDoc(doc(db, "clues", clueId), {
        caseFiles: updatedCaseFiles
      });

      setClues(prev => prev.map(c => 
        c.id === clueId 
          ? { ...c, caseFiles: updatedCaseFiles }
          : c
      ));
    } catch (err) {
      alert("Failed to delete case file. Please try again.");
    }
  }

  async function deleteSuccessImage(clueId) {
    if (!window.confirm("Delete this success image?")) return;

    try {
      await updateDoc(doc(db, "clues", clueId), {
        successImage: null
      });

      setClues(prev => prev.map(c => 
        c.id === clueId 
          ? { ...c, successImage: null }
          : c
      ));
    } catch (err) {
      alert("Failed to delete success image. Please try again.");
    }
  }

  if (loading) {
    return (
      <div className="dash-page">
        <div className="dash-loader">
          <div className="spinner"></div>
          <p>Loading clues...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dash-page">
        <div className="dash-container">
          <h1>Manage Clues</h1>
          <p className="dash-subtitle">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dash-page">
      <div className="dash-container">
        <div style={{ marginBottom: "20px" }}>
          <button
            className="dash-btn secondary"
            onClick={() => navigate(`/trailseditor/${id}/edit`)}
            style={{ width: "auto" }}
          >
            ← Back to Trail
          </button>
        </div>

        <header className="dash-header">
          <div className="dash-header-content">
            <h1 className="dash-title">Manage Clues</h1>
            <p className="dash-subtitle">for {trailName}</p>
          </div>
        </header>

        <div style={{ marginBottom: "30px" }}>
          <Link
            to={`/trailseditor/${id}/clues/new`}
            className="dash-btn primary"
            style={{ display: "inline-flex", width: "auto", textDecoration: "none" }}
          >
            ➕ Add New Clue
          </Link>
        </div>

        {clues.length === 0 ? (
          <div className="empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
              <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <p>No clues found for this trail.</p>
          </div>
        ) : (
          <div className="trails-grid">
            {clues.map(clue => (
              <div key={clue.id} className="trail-card">
                <div className="trail-header">
                  <h3>Clue #{clue.order}</h3>
                </div>
                
                <p className="trail-description">
                  <strong>Question:</strong> {clue.question}
                </p>

                {/* Case Files Section */}
                <div style={{ 
                  paddingTop: "12px", 
                  borderTop: "1px solid var(--color-border)" 
                }}>
                  <h4 style={{ 
                    fontSize: "14px", 
                    fontWeight: "600", 
                    color: "var(--color-text)",
                    marginBottom: "10px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                  }}>
                    Case Files
                    <span style={{
                      background: "var(--color-success)",
                      color: "white",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontSize: "12px"
                    }}>
                      {(clue.caseFiles || []).length}
                    </span>
                  </h4>
                  {clue.caseFiles && clue.caseFiles.length > 0 ? (
                    <div style={{ 
                      display: "grid", 
                      gridTemplateColumns: "repeat(auto-fill, minmax(70px, 1fr))",
                      gap: "8px"
                    }}>
                      {clue.caseFiles.map((fileUrl, index) => (
                        <div key={index} style={{ position: "relative" }}>
                          <img 
                            src={fileUrl} 
                            alt={`Case file ${index + 1}`}
                            style={{
                              width: "100%",
                              height: "70px",
                              objectFit: "cover",
                              borderRadius: "8px",
                              border: "1px solid var(--color-border)"
                            }}
                          />
                          <button
                            onClick={() => deleteCaseFile(clue.id, fileUrl)}
                            style={{
                              position: "absolute",
                              top: "-6px",
                              right: "-6px",
                              width: "20px",
                              height: "20px",
                              borderRadius: "50%",
                              background: "var(--color-error)",
                              color: "white",
                              border: "none",
                              cursor: "pointer",
                              fontSize: "12px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}
                            title="Delete case file"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: "var(--color-text-secondary)", fontSize: "13px" }}>
                      No case files
                    </p>
                  )}
                </div>

                {/* Success Image Section */}
                <div style={{ 
                  paddingTop: "12px", 
                  borderTop: "1px solid var(--color-border)",
                  marginTop: "12px"
                }}>
                  <h4 style={{ 
                    fontSize: "14px", 
                    fontWeight: "600", 
                    color: "var(--color-text)",
                    marginBottom: "10px"
                  }}>
                    Success Image
                  </h4>
                  {clue.successImage ? (
                    <div style={{ position: "relative", display: "inline-block" }}>
                      <img 
                        src={clue.successImage} 
                        alt="Success celebration"
                        style={{
                          width: "100px",
                          height: "70px",
                          objectFit: "cover",
                          borderRadius: "8px",
                          border: "1px solid var(--color-border)"
                        }}
                      />
                      <button
                        onClick={() => deleteSuccessImage(clue.id)}
                        style={{
                          position: "absolute",
                          top: "-6px",
                          right: "-6px",
                          width: "20px",
                          height: "20px",
                          borderRadius: "50%",
                          background: "var(--color-error)",
                          color: "white",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                        title="Delete success image"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <p style={{ color: "var(--color-text-secondary)", fontSize: "13px" }}>
                      No success image
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="trail-actions">
                  <Link
                    to={`/trailseditor/${id}/clues/${clue.id}/edit`}
                    className="dash-btn secondary"
                    style={{ textDecoration: "none" }}
                  >
                    Edit
                  </Link>
                  <button
                    className="dash-btn secondary"
                    onClick={() => deleteClue(clue.id)}
                    style={{ 
                      background: "rgba(239, 68, 68, 0.1)",
                      borderColor: "rgba(239, 68, 68, 0.3)",
                      color: "var(--color-error)"
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}